import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  request,
  type V3ApiSuccessData,
  type V3ApiError,
} from 'services/v3ApiClient';
import { type MapEmbeddingConfigObject } from 'data/v3/data-contracts';

import { MAP_EMBEDDING_CONFIGS_QUERY_KEY } from '../constants';
import { type ReviewProfileParams } from '../types';

type UpdateMapEmbeddingConfigResponse =
  V3ApiSuccessData<MapEmbeddingConfigObject>;

export const useUpdateMapEmbeddingConfig = ({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (response: UpdateMapEmbeddingConfigResponse) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  const queryClient = useQueryClient();

  type MutationFnParams = {
    mapEmbeddingConfigId: number;
    params: ReviewProfileParams;
  };

  return useMutation<
    UpdateMapEmbeddingConfigResponse,
    V3ApiError,
    MutationFnParams
  >({
    mutationFn: async ({ mapEmbeddingConfigId, params }: MutationFnParams) => {
      const response = await request({
        url: `companies/map_embedding_configs/${mapEmbeddingConfigId}`,
        method: 'put',
        data: {
          map_embedding_config: params,
        },
      });
      return response.data;
    },
    onSuccess: (response: UpdateMapEmbeddingConfigResponse) => {
      queryClient.invalidateQueries({
        queryKey: [MAP_EMBEDDING_CONFIGS_QUERY_KEY],
      });
      onSuccess?.(response);
    },
    onError: (error: V3ApiError) => {
      onError?.(error);
    },
  });
};
